import React, { FC } from 'react';
import { Box, Grid, LinearProgress, Typography } from '@material-ui/core';
import userImage from 'src/images/user.png';
import { capitalize } from 'src/utils/StringUtils';
import Card from 'src/components/Card';
import { createUseStyles } from 'src/theming';
import { LegacyUser } from 'src/interfaces/api/User';
import { LegacyEmployee } from 'src/interfaces/api/Employee';
import RankProgress from './RankProgress';
import RankImage from './RankImage';
import useRank from '../hooks/useRank';

const useStyles = createUseStyles({
  root: {
    overflow: 'visible !important',
  },
  profilePictureContainer: {
    width: 128,
    height: 128,
    marginTop: -83,
    borderRadius: '25%',
    borderWidth: 4,
    background: 'white',
    borderColor: 'white',
    borderStyle: 'solid',
    overflow: 'hidden',
    boxShadow: '0px 7px 38px -9px rgba(0,0,0,0.3)',
  },
  profilePicture: {
    width: '100%',
    height: '100%',
    textAlign: 'center',
    objectFit: 'cover',
    color: 'transparent',
  },
  information: {
    color: 'gray',
    textAlign: 'center',
    marginTop: '10px !important',
    marginBottom: '16px !important',
    '& > span': {
      lineHeight: 'initial',
    },
  },
  loader: {
    marginBottom: '-20px !important',
  },
  justifyCenter: {
    display: 'flex',
    justifyContent: 'center',
  },
  chip: {
    height: 22,
    minWidth: 22,
    lineHeight: 0,
    borderRadius: 6,
    alignItems: 'center',
    whiteSpace: 'nowrap',
    display: 'inline-flex',
    justifyContent: 'center',
    padding: '0px 8px',
    color: 'rgb(183, 33, 54)',
    fontSize: '0.75rem',
    backgroundColor: 'rgba(255, 72, 66, 0.16)',
    fontWeight: 700,
    textTransform: 'uppercase',
  },
  textCenter: {
    textAlign: 'center',
  },
});

interface Props {
  user?: LegacyUser & {
    typeUser: Omit<LegacyEmployee, 'user'>;
  };
  loading?: boolean;
}

const HeaderCard: FC<Props> = ({ user, loading }) => {
  const classes = useStyles();
  const { ranks, userRankIndex } = useRank();

  return (
    <Card className={classes.root}>
      <Grid justifyContent='center' container spacing={1}>
        <Grid item xs={12}>
          <Grid container justifyContent='center'>
            <Grid item className={classes.justifyCenter}>
              <Box position='relative'>
                <RankImage
                  documentId={undefined !== userRankIndex ? ranks[userRankIndex].logoDocumentId : undefined}
                  position='absolute'
                  boxShadow={3}
                  right={-16}
                  bottom={-10}
                />

                <div className={classes.profilePictureContainer}>
                  <img
                    className={classes.profilePicture}
                    src={user?.typeUser?.profileImage ? '/' + user.typeUser.profileImage : userImage}
                    onError={({ currentTarget }) => {
                      currentTarget.src = userImage;
                    }}
                    alt='profile picture'
                  />
                </div>
              </Box>
            </Grid>
          </Grid>
        </Grid>

        <Grid className={classes.information} item xs={12}>
          <Typography variant='h6'>
            {capitalize(`${user?.typeUser?.firstname || ''} ${user?.typeUser?.lastname || ''}`.toLowerCase())}
          </Typography>
          <Typography variant='overline'>{undefined !== userRankIndex ? ranks[userRankIndex].name : ''}</Typography>
        </Grid>

        {!user?.enabled && !loading && (
          <Grid item xs={12} className={classes.textCenter}>
            <div className={classes.chip}>Compte désactivé</div>
          </Grid>
        )}

        <Grid item xs={12}>
          <RankProgress />
        </Grid>

        {loading && (
          <Grid item xs={12} className={classes.loader}>
            <LinearProgress />
          </Grid>
        )}
      </Grid>
    </Card>
  );
};

export default HeaderCard;
